import { useEffect, useState } from "react";
import { api, rupees, CATEGORIES } from "@/lib/api";
import Header from "@/components/Header";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Pencil, Trash2, Package, TrendingUp, Pill, AlertTriangle, IndianRupee, ShieldAlert, FileCheck2, FileX2, Clock, Tag, Bell, Sparkles, Send, Search, FlaskConical, Inbox, Phone, MessageCircle, Copy, ExternalLink, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const EMPTY_MED = {
  name: "", brand: "", category: "pain-relief", description: "",
  price: "", mrp: "", stock: "", image_url: "", dosage: "", salt_name: "", prescription_required: false,
};

const STATUSES = ["confirmed", "packed", "dispatched", "delivered", "cancelled"];

export default function Admin() {
  const [stats, setStats] = useState(null);

  useEffect(() => { loadStats(); }, []);
  const loadStats = () => api.get("/admin/stats").then(({ data }) => setStats(data));

  return (
    <div className="min-h-screen bg-[#EFE7D6]">
      <Header/>
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="label-caps text-stone-500">Console</div>
            <h1 className="font-serif-display text-3xl text-[#1A362B] tracking-tight mt-1">Admin Dashboard</h1>
          </div>
        </div>

        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <StatCard icon={IndianRupee} label="Revenue" value={rupees(stats.revenue)} testid="stat-revenue"/>
            <StatCard icon={Package} label="Pending Orders" value={stats.pending_orders} testid="stat-pending"/>
            <StatCard icon={TrendingUp} label="Delivered" value={stats.delivered_orders} testid="stat-delivered"/>
            <StatCard icon={Pill} label="SKUs" value={stats.total_medicines} sub={`${stats.low_stock} low stock`} testid="stat-skus"/>
          </div>
        )}

        <Tabs defaultValue="orders" className="w-full">
          <TabsList className="bg-transparent border-b border-stone-200 rounded-none w-full justify-start h-auto p-0 gap-6 overflow-x-auto thin-scroll">
            <TabsTrigger value="orders" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#1A362B] data-[state=active]:bg-transparent px-0 pb-3 label-caps" data-testid="tab-orders">Orders</TabsTrigger>
            <TabsTrigger value="rx-requests" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#1A362B] data-[state=active]:bg-transparent px-0 pb-3 label-caps" data-testid="tab-rx-requests">Rx Requests</TabsTrigger>
            <TabsTrigger value="product-requests" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#1A362B] data-[state=active]:bg-transparent px-0 pb-3 label-caps" data-testid="tab-product-requests">Wanted</TabsTrigger>
            <TabsTrigger value="inventory" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#1A362B] data-[state=active]:bg-transparent px-0 pb-3 label-caps" data-testid="tab-inventory">Inventory</TabsTrigger>
            <TabsTrigger value="coupons" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#1A362B] data-[state=active]:bg-transparent px-0 pb-3 label-caps" data-testid="tab-coupons">Coupons</TabsTrigger>
            <TabsTrigger value="reminders" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#1A362B] data-[state=active]:bg-transparent px-0 pb-3 label-caps" data-testid="tab-reminders">Refill Reminders</TabsTrigger>
            <TabsTrigger value="users" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#1A362B] data-[state=active]:bg-transparent px-0 pb-3 label-caps" data-testid="tab-users">Users</TabsTrigger>
          </TabsList>
          <TabsContent value="orders" className="pt-6"><OrdersTab onChange={loadStats}/></TabsContent>
          <TabsContent value="rx-requests" className="pt-6"><RxRequestsTab/></TabsContent>
          <TabsContent value="product-requests" className="pt-6"><ProductRequestsTab/></TabsContent>
          <TabsContent value="inventory" className="pt-6"><InventoryTab onChange={loadStats}/></TabsContent>
          <TabsContent value="coupons" className="pt-6"><CouponsTab/></TabsContent>
          <TabsContent value="reminders" className="pt-6"><RemindersTab/></TabsContent>
          <TabsContent value="users" className="pt-6"><UsersTab/></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub, testid }) {
  return (
    <div className="p-5 border border-stone-200 rounded-md bg-white" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="label-caps text-xs text-stone-500">{label}</div>
        <Icon className="w-4 h-4 text-[#1A362B]"/>
      </div>
      <div className="font-serif-display text-3xl text-[#1A362B] mt-2 font-mono-num">{value}</div>
      {sub && <div className="text-xs text-stone-500 mt-1">{sub}</div>}
    </div>
  );
}

function OrdersTab({ onChange }) {
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState("all");
  const [users, setUsers] = useState([]);
  const [rxOrder, setRxOrder] = useState(null);
  const load = () => api.get("/admin/orders", { params: filter !== "all" ? { status: filter } : {} }).then(({ data }) => setOrders(data));
  useEffect(() => { load(); }, [filter]);
  useEffect(() => { api.get("/admin/users").then(({ data }) => setUsers(data)); }, []);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/orders/${id}/status`, { status });
      toast.success(`Order marked ${status}`);
      load(); onChange && onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update status");
    }
  };
  const assign = async (id, delivery_user_id) => {
    await api.patch(`/admin/orders/${id}/assign`, { delivery_user_id });
    toast.success("Delivery assigned");
    load();
  };

  const deliveryUsers = users.filter((u) => u.role === "delivery" || u.role === "admin");

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-4">
        {["all", ...STATUSES].map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            className={`px-3 py-1.5 text-xs label-caps rounded-sm border transition-colors ${filter === s ? "bg-[#1A362B] text-[#EFE7D6] border-[#1A362B]" : "bg-white text-stone-600 border-stone-300 hover:border-[#1A362B]"}`}
            data-testid={`filter-${s}`}
          >{s}</button>
        ))}
      </div>
      <div className="border border-stone-200 rounded-md bg-white overflow-auto thin-scroll">
        <Table>
          <TableHeader>
            <TableRow className="border-stone-200">
              <TableHead className="label-caps text-[10px]">Order</TableHead>
              <TableHead className="label-caps text-[10px]">Customer</TableHead>
              <TableHead className="label-caps text-[10px]">Total</TableHead>
              <TableHead className="label-caps text-[10px]">Payment</TableHead>
              <TableHead className="label-caps text-[10px]">Rx</TableHead>
              <TableHead className="label-caps text-[10px]">Status</TableHead>
              <TableHead className="label-caps text-[10px]">Delivery</TableHead>
              <TableHead className="label-caps text-[10px] text-right">Set status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {orders.map((o) => (
              <TableRow key={o.id} className="border-stone-200" data-testid={`admin-order-${o.id}`}>
                <TableCell>
                  <div className="font-mono-num text-sm">#{o.order_no}</div>
                  <div className="text-xs text-stone-500">{new Date(o.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}</div>
                </TableCell>
                <TableCell>
                  <div className="text-sm">{o.customer_name}</div>
                  <div className="text-xs text-stone-500">{o.address.city} · {o.address.phone}</div>
                </TableCell>
                <TableCell className="font-mono-num">{rupees(o.total)}</TableCell>
                <TableCell><span className="text-xs uppercase tracking-wider">{o.payment_method}</span></TableCell>
                <TableCell>
                  {o.prescription_path ? (
                    <button
                      onClick={() => setRxOrder(o)}
                      className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-sm border transition-colors ${
                        o.prescription_status === "approved" ? "border-[#1A362B] text-[#1A362B]" :
                        o.prescription_status === "rejected" ? "border-[#C44949] text-[#C44949]" :
                        "border-[#D96C4A] text-[#D96C4A] animate-pulse"
                      }`}
                      data-testid={`rx-btn-${o.id}`}
                    >
                      {o.prescription_status === "approved" ? <FileCheck2 className="w-3 h-3"/> :
                       o.prescription_status === "rejected" ? <FileX2 className="w-3 h-3"/> :
                       <Clock className="w-3 h-3"/>}
                      {o.prescription_status || "review"}
                    </button>
                  ) : (
                    <span className="text-xs text-stone-400">—</span>
                  )}
                </TableCell>
                <TableCell>
                  <Badge className={`status-${o.status} rounded-sm border-0 capitalize`}>{o.status}</Badge>
                </TableCell>
                <TableCell>
                  <Select value={o.delivery_user_id || ""} onValueChange={(v) => assign(o.id, v)}>
                    <SelectTrigger className="h-8 text-xs w-36" data-testid={`assign-${o.id}`}><SelectValue placeholder="Unassigned"/></SelectTrigger>
                    <SelectContent>
                      {deliveryUsers.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name} ({u.role})</SelectItem>)}
                    </SelectContent>
                  </Select>
                </TableCell>
                <TableCell className="text-right">
                  <Select value={o.status} onValueChange={(v) => updateStatus(o.id, v)}>
                    <SelectTrigger className="h-8 text-xs w-32 ml-auto" data-testid={`status-${o.id}`}><SelectValue/></SelectTrigger>
                    <SelectContent>
                      {STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </TableCell>
              </TableRow>
            ))}
            {orders.length === 0 && (
              <TableRow><TableCell colSpan={8} className="text-center text-stone-500 py-10">No orders here</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <PrescriptionDialog order={rxOrder} onClose={() => setRxOrder(null)} onDone={() => { setRxOrder(null); load(); }}/>
    </div>
  );
}

function PrescriptionDialog({ order, onClose, onDone }) {
  const [url, setUrl] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setUrl(null); setNote(order?.prescription_note || "");
    if (!order?.prescription_path) return;
    let objUrl;
    (async () => {
      try {
        const r = await api.get(`/prescriptions/file/${order.prescription_path}`, { responseType: "blob" });
        objUrl = URL.createObjectURL(r.data);
        setUrl(objUrl);
      } catch (err) {
        console.debug("Prescription image load failed:", err?.message);
      }
    })();
    return () => { if (objUrl) URL.revokeObjectURL(objUrl); };
  }, [order?.prescription_path]);

  const decide = async (decision) => {
    if (!order) return;
    setBusy(true);
    try {
      await api.patch(`/admin/orders/${order.id}/prescription`, { decision, note });
      toast.success(`Prescription ${decision}`);
      onDone && onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!order} onOpenChange={(open) => !open && onClose && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-2xl text-[#1A362B] flex items-center gap-2">
            <ShieldAlert className="w-5 h-5"/> Prescription review · #{order?.order_no}
          </DialogTitle>
        </DialogHeader>
        {order && (
          <div className="grid md:grid-cols-2 gap-6 py-2">
            <div>
              <div className="label-caps text-[10px] text-stone-500 mb-2">Uploaded file</div>
              <div className="border border-stone-200 rounded-md overflow-hidden bg-[#FFFFFF] min-h-[280px] flex items-center justify-center">
                {url ? (
                  order.prescription_path?.toLowerCase().endsWith(".pdf") ? (
                    <a href={url} target="_blank" rel="noreferrer" className="text-[#1A362B] underline">Open PDF</a>
                  ) : (
                    <img src={url} alt="Prescription" className="w-full max-h-[420px] object-contain"/>
                  )
                ) : (
                  <div className="text-sm text-stone-500">Loading…</div>
                )}
              </div>
              {url && <a href={url} target="_blank" rel="noreferrer" className="mt-2 inline-block text-xs text-[#D96C4A] hover:text-[#1A362B]">Open full size ↗</a>}
            </div>
            <div>
              {order.prescription_ai && (
                <div className="mb-4 p-4 rounded-md border border-[#1A362B]/30 bg-[#1A362B]/[0.03]" data-testid="rx-ai-panel">
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles className="w-4 h-4 text-[#1A362B]"/>
                    <div className="label-caps text-[10px] text-stone-500">AI pre-scan · Gemini 3 Flash</div>
                  </div>
                  {order.prescription_ai.available === false ? (
                    <div className="text-xs text-stone-600">AI verify not available: {order.prescription_ai.error}</div>
                  ) : (
                    <div className="space-y-1 text-xs">
                      <div><span className="text-stone-500">Looks like a prescription:</span> <span className={order.prescription_ai.is_prescription ? "text-[#1A362B] font-medium" : "text-[#C44949] font-medium"}>{String(order.prescription_ai.is_prescription)}</span></div>
                      {order.prescription_ai.doctor_name && <div><span className="text-stone-500">Doctor:</span> {order.prescription_ai.doctor_name}</div>}
                      {order.prescription_ai.patient_name && <div><span className="text-stone-500">Patient:</span> {order.prescription_ai.patient_name}</div>}
                      {order.prescription_ai.date && <div><span className="text-stone-500">Date:</span> {order.prescription_ai.date}</div>}
                      {Array.isArray(order.prescription_ai.medicines) && order.prescription_ai.medicines.length > 0 && (
                        <div><span className="text-stone-500">Medicines read:</span> {order.prescription_ai.medicines.join(", ")}</div>
                      )}
                      {typeof order.prescription_ai.confidence === "number" && (
                        <div><span className="text-stone-500">Confidence:</span> <span className="font-mono-num">{(order.prescription_ai.confidence * 100).toFixed(0)}%</span></div>
                      )}
                      {Array.isArray(order.prescription_ai.issues) && order.prescription_ai.issues.length > 0 && (
                        <div className="mt-1 text-[#a4472b]">
                          <div className="label-caps text-[10px]">Flags</div>
                          <ul className="list-disc list-inside">{order.prescription_ai.issues.map((s, i) => <li key={`${s}-${i}`}>{s}</li>)}</ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
              <div className="label-caps text-[10px] text-stone-500 mb-2">Rx-only items in this order</div>
              <ul className="space-y-2 text-sm">
                {order.items.map((it) => (
                  <li key={it.medicine_id} className="flex items-center gap-2">
                    <span className="text-[#1A362B]">·</span>
                    <span>{it.name} × {it.quantity}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-4">
                <div className="label-caps text-[10px] text-stone-500 mb-2">Customer</div>
                <div className="text-sm">{order.customer_name} · {order.customer_email}</div>
                <div className="text-xs text-stone-500 mt-1">📞 {order.address.phone}</div>
              </div>
              <div className="mt-4">
                <Label className="text-xs label-caps text-stone-500">Note to customer (optional)</Label>
                <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder="e.g., Prescription looks valid, dispatching soon" className="mt-1" data-testid="rx-note-input"/>
              </div>
            </div>
          </div>
        )}
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button
            variant="outline"
            onClick={() => decide("rejected")}
            disabled={busy}
            className="border-[#C44949] text-[#C44949] hover:bg-[#C44949] hover:text-white"
            data-testid="rx-reject"
          >
            <FileX2 className="w-4 h-4 mr-1"/> Reject
          </Button>
          <Button
            onClick={() => decide("approved")}
            disabled={busy}
            className="bg-[#1A362B] hover:bg-[#244A3C] text-[#EFE7D6]"
            data-testid="rx-approve"
          >
            <FileCheck2 className="w-4 h-4 mr-1"/> Approve
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function InventoryTab({ onChange }) {
  const [meds, setMeds] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const load = () => api.get("/medicines").then(({ data }) => setMeds(data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(EMPTY_MED); setOpen(true); };
  const openEdit = (m) => { setEditing({ ...m, salt_name: m.salt_name || "" }); setOpen(true); };

  const save = async () => {
    const payload = {
      ...editing,
      price: parseFloat(editing.price), mrp: parseFloat(editing.mrp), stock: parseInt(editing.stock),
      salt_name: (editing.salt_name || "").trim(),
    };
    if (!payload.name || !payload.brand || !payload.image_url) { toast.error("Fill required fields"); return; }
    try {
      if (editing.id) await api.put(`/medicines/${editing.id}`, payload);
      else await api.post("/medicines", payload);
      toast.success("Saved");
      setOpen(false); load(); onChange && onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  const remove = async (id) => {
    if (!confirm("Delete this medicine?")) return;
    await api.delete(`/medicines/${id}`);
    toast.success("Deleted"); load(); onChange && onChange();
  };

  const q = search.trim().toLowerCase();
  const filtered = q
    ? meds.filter((m) =>
        (m.name || "").toLowerCase().includes(q) ||
        (m.brand || "").toLowerCase().includes(q) ||
        (m.salt_name || "").toLowerCase().includes(q)
      )
    : meds;

  return (
    <div>
      <div className="flex flex-wrap justify-between items-center gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div className="text-sm text-stone-600" data-testid="inventory-count">{filtered.length} of {meds.length} medicines</div>
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-stone-400"/>
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, brand or salt…"
              className="pl-8 h-8 w-72 text-sm"
              data-testid="inventory-search"
            />
          </div>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openNew} className="rounded-sm bg-[#1A362B] hover:bg-[#244A3C] text-[#EFE7D6] h-9" data-testid="add-medicine-btn">
              <Plus className="w-4 h-4 mr-1"/> Add Medicine
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-serif-display text-2xl text-[#1A362B]">{editing?.id ? "Edit" : "Add"} Medicine</DialogTitle></DialogHeader>
            {editing && (
              <div className="grid grid-cols-2 gap-4 py-2">
                <div className="col-span-2"><Label>Name *</Label><Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="med-name"/></div>
                <div><Label>Brand *</Label><Input value={editing.brand} onChange={(e) => setEditing({ ...editing, brand: e.target.value })} data-testid="med-brand"/></div>
                <div>
                  <Label>Category</Label>
                  <Select value={editing.category} onValueChange={(v) => setEditing({ ...editing, category: v })}>
                    <SelectTrigger data-testid="med-category"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.filter((c) => c.id !== "all").map((c) => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Price (₹) *</Label><Input type="number" value={editing.price} onChange={(e) => setEditing({ ...editing, price: e.target.value })} data-testid="med-price"/></div>
                <div><Label>MRP (₹)</Label><Input type="number" value={editing.mrp} onChange={(e) => setEditing({ ...editing, mrp: e.target.value })} data-testid="med-mrp"/></div>
                <div><Label>Stock *</Label><Input type="number" value={editing.stock} onChange={(e) => setEditing({ ...editing, stock: e.target.value })} data-testid="med-stock"/></div>
                <div><Label>Dosage</Label><Input value={editing.dosage} onChange={(e) => setEditing({ ...editing, dosage: e.target.value })} data-testid="med-dosage"/></div>
                <div className="col-span-2">
                  <Label className="flex items-center gap-1"><FlaskConical className="w-3.5 h-3.5"/> Salt / Generic name</Label>
                  <Input
                    value={editing.salt_name}
                    onChange={(e) => setEditing({ ...editing, salt_name: e.target.value })}
                    placeholder="e.g. paracetamol, amlodipine, cetirizine · comma-separate multiple salts"
                    data-testid="med-salt"
                  />
                  <p className="text-[10px] text-stone-500 mt-1">Shoppers can find this medicine by searching any of these salt names.</p>
                </div>
                <div className="col-span-2"><Label>Image URL *</Label><Input value={editing.image_url} onChange={(e) => setEditing({ ...editing, image_url: e.target.value })} data-testid="med-image"/></div>
                <div className="col-span-2"><Label>Description</Label><Textarea value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} rows={3} data-testid="med-desc"/></div>
                <div className="col-span-2 flex items-center gap-2">
                  <Checkbox checked={editing.prescription_required} onCheckedChange={(v) => setEditing({ ...editing, prescription_required: !!v })} id="rx" data-testid="med-rx"/>
                  <Label htmlFor="rx" className="cursor-pointer">Prescription required</Label>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={save} className="bg-[#1A362B] hover:bg-[#244A3C] text-[#EFE7D6]" data-testid="med-save">Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <div className="border border-stone-200 rounded-md bg-white overflow-auto thin-scroll">
        <Table>
          <TableHeader>
            <TableRow className="border-stone-200">
              <TableHead className="label-caps text-[10px]">Medicine</TableHead>
              <TableHead className="label-caps text-[10px]">Salt / Generic</TableHead>
              <TableHead className="label-caps text-[10px]">Category</TableHead>
              <TableHead className="label-caps text-[10px]">Price</TableHead>
              <TableHead className="label-caps text-[10px]">Stock</TableHead>
              <TableHead className="label-caps text-[10px]">Rx</TableHead>
              <TableHead className="label-caps text-[10px] text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((m) => (
              <TableRow key={m.id} className="border-stone-200" data-testid={`inv-row-${m.id}`}>
                <TableCell>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#FFFFFF] rounded-sm overflow-hidden shrink-0"><img src={m.image_url} alt="" className="w-full h-full object-cover"/></div>
                    <div>
                      <div className="text-sm text-[#1A362B]">{m.name}</div>
                      <div className="text-xs text-stone-500">{m.brand}</div>
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  {m.salt_name ? (
                    <span className="text-xs text-[#1A362B] italic font-serif-display" data-testid={`salt-${m.id}`}>{m.salt_name}</span>
                  ) : (
                    <span className="text-xs text-stone-400 label-caps">—</span>
                  )}
                </TableCell>
                <TableCell><span className="text-xs uppercase tracking-wider">{m.category.replace("-", " ")}</span></TableCell>
                <TableCell className="font-mono-num">{rupees(m.price)}</TableCell>
                <TableCell>
                  <span className={`font-mono-num text-sm ${m.stock <= 10 ? "text-[#C44949]" : "text-[#1A362B]"}`}>
                    {m.stock} {m.stock <= 10 && <AlertTriangle className="inline w-3 h-3 ml-1"/>}
                  </span>
                </TableCell>
                <TableCell>{m.prescription_required ? <Badge className="bg-[#1A362B] text-[#EFE7D6] rounded-sm border-0">Rx</Badge> : <span className="text-stone-400 text-xs">—</span>}</TableCell>
                <TableCell className="text-right space-x-1">
                  <Button size="icon" variant="ghost" onClick={() => openEdit(m)} data-testid={`edit-${m.id}`}><Pencil className="w-4 h-4"/></Button>
                  <Button size="icon" variant="ghost" onClick={() => remove(m.id)} data-testid={`del-${m.id}`}><Trash2 className="w-4 h-4 text-[#C44949]"/></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function UsersTab() {
  const [users, setUsers] = useState([]);
  const load = () => api.get("/admin/users").then(({ data }) => setUsers(data));
  useEffect(() => { load(); }, []);

  const changeRole = async (user_id, role) => {
    await api.patch(`/admin/users/${user_id}/role`, { role });
    toast.success("Role updated"); load();
  };

  return (
    <div className="border border-stone-200 rounded-md bg-white overflow-auto thin-scroll">
      <Table>
        <TableHeader>
          <TableRow className="border-stone-200">
            <TableHead className="label-caps text-[10px]">User</TableHead>
            <TableHead className="label-caps text-[10px]">Email</TableHead>
            <TableHead className="label-caps text-[10px]">Joined</TableHead>
            <TableHead className="label-caps text-[10px] text-right">Role</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {users.map((u) => (
            <TableRow key={u.user_id} className="border-stone-200">
              <TableCell>
                <div className="flex items-center gap-3">
                  {u.picture ? <img src={u.picture} alt="" className="w-8 h-8 rounded-full"/> : <div className="w-8 h-8 rounded-full bg-[#1A362B] text-[#EFE7D6] flex items-center justify-center text-xs">{u.name?.[0]}</div>}
                  <span>{u.name}</span>
                </div>
              </TableCell>
              <TableCell className="text-sm text-stone-600">{u.email}</TableCell>
              <TableCell className="text-xs text-stone-500">{new Date(u.created_at).toLocaleDateString("en-IN")}</TableCell>
              <TableCell className="text-right">
                <Select value={u.role} onValueChange={(v) => changeRole(u.user_id, v)}>
                  <SelectTrigger className="h-8 text-xs w-32 ml-auto" data-testid={`role-${u.user_id}`}><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="customer">Customer</SelectItem>
                    <SelectItem value="delivery">Delivery</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function CouponsTab() {
  const [coupons, setCoupons] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ code: "", discount_type: "percent", value: "", min_order: "", max_discount: "", usage_limit: "", expires_at: "" });
  const load = () => api.get("/admin/coupons").then(({ data }) => setCoupons(data));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.code || !form.value) { toast.error("Code and value required"); return; }
    try {
      await api.post("/admin/coupons", {
        code: form.code.toUpperCase(),
        discount_type: form.discount_type,
        value: parseFloat(form.value),
        min_order: parseFloat(form.min_order) || 0,
        max_discount: parseFloat(form.max_discount) || 0,
        usage_limit: parseInt(form.usage_limit) || 0,
        expires_at: form.expires_at || null,
        active: true,
      });
      toast.success("Coupon created"); setOpen(false);
      setForm({ code: "", discount_type: "percent", value: "", min_order: "", max_discount: "", usage_limit: "", expires_at: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    }
  };

  const remove = async (id) => {
    if (!confirm("Delete coupon?")) return;
    await api.delete(`/admin/coupons/${id}`);
    toast.success("Deleted"); load();
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <div className="text-sm text-stone-600">{coupons.length} coupons</div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-sm bg-[#1A362B] hover:bg-[#244A3C] text-[#EFE7D6] h-9" data-testid="add-coupon-btn">
              <Plus className="w-4 h-4 mr-1"/> New Coupon
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle className="font-serif-display text-2xl text-[#1A362B]">New coupon</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-4 py-2">
              <div className="col-span-2"><Label>Code *</Label><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="FIRST10" data-testid="coup-code"/></div>
              <div>
                <Label>Type</Label>
                <Select value={form.discount_type} onValueChange={(v) => setForm({ ...form, discount_type: v })}>
                  <SelectTrigger data-testid="coup-type"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percent">Percent %</SelectItem>
                    <SelectItem value="flat">Flat ₹</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Value *</Label><Input type="number" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} placeholder="10" data-testid="coup-value"/></div>
              <div><Label>Min order ₹</Label><Input type="number" value={form.min_order} onChange={(e) => setForm({ ...form, min_order: e.target.value })} placeholder="0"/></div>
              <div><Label>Max discount ₹ (percent only)</Label><Input type="number" value={form.max_discount} onChange={(e) => setForm({ ...form, max_discount: e.target.value })} placeholder="0 = no cap"/></div>
              <div><Label>Usage limit</Label><Input type="number" value={form.usage_limit} onChange={(e) => setForm({ ...form, usage_limit: e.target.value })} placeholder="0 = unlimited"/></div>
              <div><Label>Expires at</Label><Input type="date" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })}/></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={create} className="bg-[#1A362B] hover:bg-[#244A3C] text-[#EFE7D6]" data-testid="coup-save">Create</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <div className="border border-stone-200 rounded-md bg-white overflow-auto thin-scroll">
        <Table>
          <TableHeader>
            <TableRow className="border-stone-200">
              <TableHead className="label-caps text-[10px]">Code</TableHead>
              <TableHead className="label-caps text-[10px]">Discount</TableHead>
              <TableHead className="label-caps text-[10px]">Min order</TableHead>
              <TableHead className="label-caps text-[10px]">Uses</TableHead>
              <TableHead className="label-caps text-[10px]">Expires</TableHead>
              <TableHead className="label-caps text-[10px] text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {coupons.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-stone-500 py-10">No coupons yet. Try <b>FIRST10</b> or <b>WELLNESS100</b>.</TableCell></TableRow>}
            {coupons.map((c) => (
              <TableRow key={c.id} className="border-stone-200">
                <TableCell><span className="font-mono-num text-sm text-[#1A362B] flex items-center gap-2"><Tag className="w-3 h-3"/> {c.code}</span></TableCell>
                <TableCell><span className="text-sm">{c.discount_type === "percent" ? `${c.value}%` : `₹${c.value}`}{c.max_discount ? ` (cap ₹${c.max_discount})` : ""}</span></TableCell>
                <TableCell className="font-mono-num text-sm">₹{c.min_order}</TableCell>
                <TableCell className="font-mono-num text-sm">{c.uses}{c.usage_limit ? ` / ${c.usage_limit}` : ""}</TableCell>
                <TableCell className="text-xs text-stone-500">{c.expires_at ? new Date(c.expires_at).toLocaleDateString("en-IN") : "—"}</TableCell>
                <TableCell className="text-right">
                  <Button size="icon" variant="ghost" onClick={() => remove(c.id)} data-testid={`coup-del-${c.id}`}><Trash2 className="w-4 h-4 text-[#C44949]"/></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function RemindersTab() {
  const [reminders, setReminders] = useState([]);
  const [busy, setBusy] = useState(false);
  const [waEnabled, setWaEnabled] = useState(true);
  const load = () => api.get("/admin/reminders").then(({ data }) => setReminders(data));
  useEffect(() => {
    load();
    api.get("/config").then(({ data }) => setWaEnabled(!!data.whatsapp_enabled)).catch(() => {});
  }, []);

  const sendDue = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/reminders/send-due");
      toast.success(`Sent ${data.sent} due reminders`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const sendOne = async (id) => {
    try {
      await api.post(`/admin/reminders/${id}/send-now`);
      toast.success("Reminder sent (WhatsApp)");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const now = new Date();

  return (
    <div>
      {!waEnabled && (
        <div className="mb-4 p-3 rounded-md border-l-4 border-[#D96C4A] bg-[#D96C4A]/10 text-xs text-[#a4472b]" data-testid="wa-warning">
          WhatsApp is not connected — add <code className="font-mono-num">TWILIO_ACCOUNT_SID</code> and <code className="font-mono-num">TWILIO_AUTH_TOKEN</code> to <code>backend/.env</code> to enable real sends. Reminder scheduling still works.
        </div>
      )}
      <div className="flex justify-between items-center mb-4">
        <div className="text-sm text-stone-600">{reminders.length} reminders scheduled</div>
        <Button onClick={sendDue} disabled={busy} className="rounded-sm bg-[#1A362B] hover:bg-[#244A3C] text-[#EFE7D6] h-9" data-testid="send-due-btn">
          <Send className="w-4 h-4 mr-1"/> {busy ? "Sending…" : "Send all due"}
        </Button>
      </div>
      <div className="border border-stone-200 rounded-md bg-white overflow-auto thin-scroll">
        <Table>
          <TableHeader>
            <TableRow className="border-stone-200">
              <TableHead className="label-caps text-[10px]">Customer</TableHead>
              <TableHead className="label-caps text-[10px]">Items</TableHead>
              <TableHead className="label-caps text-[10px]">Phone</TableHead>
              <TableHead className="label-caps text-[10px]">Due</TableHead>
              <TableHead className="label-caps text-[10px]">Sent</TableHead>
              <TableHead className="label-caps text-[10px] text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {reminders.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-stone-500 py-10">No refill reminders yet. They are auto-scheduled 28 days after delivery of any chronic-care order.</TableCell></TableRow>}
            {reminders.map((r) => {
              const due = new Date(r.due_at);
              const isDue = !r.sent_at && due <= now;
              return (
                <TableRow key={r.id} className="border-stone-200">
                  <TableCell><div className="text-sm">{r.customer_name}</div><div className="text-xs text-stone-500">#{r.order_no}</div></TableCell>
                  <TableCell><div className="text-xs">{(r.items || []).slice(0, 2).join(", ")}{r.items?.length > 2 ? ` +${r.items.length - 2}` : ""}</div></TableCell>
                  <TableCell className="text-sm">{r.phone || "—"}</TableCell>
                  <TableCell>
                    <div className={`text-xs ${isDue ? "text-[#D96C4A] font-semibold" : "text-stone-600"}`}>
                      {due.toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}
                      {isDue && <span className="ml-1 label-caps text-[9px]">(due)</span>}
                    </div>
                  </TableCell>
                  <TableCell>
                    {r.sent_at ? <Badge className="status-delivered rounded-sm border-0 text-[10px]">{r.sent_ok ? "sent" : "attempted"}</Badge> : <Badge className="status-packed rounded-sm border-0 text-[10px]">pending</Badge>}
                  </TableCell>
                  <TableCell className="text-right">
                    {!r.sent_at && (
                      <Button size="sm" variant="ghost" onClick={() => sendOne(r.id)} className="h-7 text-xs" data-testid={`send-${r.id}`}>
                        <Bell className="w-3 h-3 mr-1"/> Send
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

const RX_STATUS_META = {
  pending:   { label: "Pending",   className: "status-packed" },
  contacted: { label: "Contacted", className: "status-dispatched" },
  fulfilled: { label: "Fulfilled", className: "status-delivered" },
  ignored:   { label: "Ignored",   className: "bg-stone-300 text-stone-700" },
};

function RxRequestsTab() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);

  const load = () => {
    setBusy(true);
    api.get("/admin/prescription-requests", { params: filter !== "all" ? { status: filter } : {} })
      .then(({ data }) => setRows(data))
      .finally(() => setBusy(false));
  };
  useEffect(() => { load(); }, [filter]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/prescription-requests/${id}`, { status });
      toast.success(`Marked ${status}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  const copyPhone = (p) => {
    navigator.clipboard?.writeText(p).then(() => toast.success(`Copied +91 ${p}`)).catch(() => {});
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex flex-wrap gap-2 items-center">
          {["pending", "contacted", "fulfilled", "ignored", "all"].map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              className={`px-3 py-1.5 text-xs label-caps rounded-sm border transition-colors ${filter === s ? "bg-[#1A362B] text-[#EFE7D6] border-[#1A362B]" : "bg-white text-stone-600 border-stone-300 hover:border-[#1A362B]"}`}
              data-testid={`rx-filter-${s}`}
            >
              {s}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-stone-600" data-testid="rx-count">{rows.length} request{rows.length === 1 ? "" : "s"}</span>
          <Button size="sm" variant="outline" onClick={load} disabled={busy} className="h-8" data-testid="rx-refresh">
            <RefreshCw className={`w-3.5 h-3.5 mr-1 ${busy ? "animate-spin" : ""}`}/> Refresh
          </Button>
        </div>
      </div>

      <div className="border border-stone-200 rounded-md bg-white overflow-auto thin-scroll">
        <Table>
          <TableHeader>
            <TableRow className="border-stone-200">
              <TableHead className="label-caps text-[10px]">Prescription</TableHead>
              <TableHead className="label-caps text-[10px]">Customer</TableHead>
              <TableHead className="label-caps text-[10px]">AI verify</TableHead>
              <TableHead className="label-caps text-[10px]">Received</TableHead>
              <TableHead className="label-caps text-[10px]">Status</TableHead>
              <TableHead className="label-caps text-[10px] text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 && (
              <TableRow><TableCell colSpan={6} className="text-center text-stone-500 py-10">
                <Inbox className="w-6 h-6 mx-auto mb-2 opacity-40"/>
                No {filter !== "all" ? filter : ""} prescription requests. Shoppers upload from the landing page — they land here automatically.
              </TableCell></TableRow>
            )}
            {rows.map((r) => {
              const meta = RX_STATUS_META[r.status] || RX_STATUS_META.pending;
              const ai = r.ai_result || {};
              const aiOk = ai && (ai.is_prescription === true || ai.valid === true);
              const aiBad = ai && (ai.is_prescription === false || ai.valid === false);
              const received = new Date(r.created_at);
              const isPdf = r.prescription_path?.toLowerCase().endsWith(".pdf");
              const waLink = `https://wa.me/91${r.phone}`;
              return (
                <TableRow key={r.id} className="border-stone-200" data-testid={`rx-row-${r.id}`}>
                  <TableCell>
                    <button
                      onClick={() => setPreview(r)}
                      className="w-14 h-14 bg-stone-100 rounded-sm overflow-hidden border border-stone-200 hover:border-[#1A362B] transition-colors flex items-center justify-center"
                      data-testid={`rx-thumb-${r.id}`}
                    >
                      {isPdf ? (
                        <div className="text-[10px] label-caps text-[#1A362B]">PDF</div>
                      ) : (
                        <RxThumb path={r.prescription_path} />
                      )}
                    </button>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm text-[#1A362B]">{r.name}</div>
                    <div className="text-xs text-stone-500 font-mono-num flex items-center gap-1">
                      <Phone className="w-3 h-3"/> +91 {r.phone}
                    </div>
                    {r.note && <div className="text-[11px] text-stone-500 italic mt-1 max-w-[220px] truncate" title={r.note}>{r.note}</div>}
                  </TableCell>
                  <TableCell>
                    {!r.ai_result ? (
                      <Badge variant="outline" className="text-[10px] rounded-sm border-stone-300 text-stone-500">Analysing…</Badge>
                    ) : aiOk ? (
                      <Badge className="status-delivered rounded-sm border-0 text-[10px] gap-1"><FileCheck2 className="w-3 h-3"/> Verified</Badge>
                    ) : aiBad ? (
                      <Badge className="bg-[#D96C4A] text-white rounded-sm border-0 text-[10px] gap-1"><FileX2 className="w-3 h-3"/> Not Rx</Badge>
                    ) : (
                      <Badge variant="outline" className="text-[10px] rounded-sm border-stone-300 text-stone-600">Review</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="text-xs text-stone-600">{received.toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}</div>
                    <div className="text-[10px] text-stone-400">{received.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}</div>
                  </TableCell>
                  <TableCell>
                    <Badge className={`${meta.className} rounded-sm border-0 text-[10px] capitalize`}>{meta.label}</Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="inline-flex items-center gap-1">
                      <a href={waLink} target="_blank" rel="noopener noreferrer" data-testid={`rx-wa-${r.id}`}>
                        <Button size="icon" variant="ghost" className="h-8 w-8" title="Open WhatsApp"><MessageCircle className="w-4 h-4 text-[#25D366]"/></Button>
                      </a>
                      <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => copyPhone(r.phone)} title="Copy number" data-testid={`rx-copy-${r.id}`}><Copy className="w-4 h-4"/></Button>
                      <Select value={r.status} onValueChange={(v) => updateStatus(r.id, v)}>
                        <SelectTrigger className="h-8 w-[120px] text-xs" data-testid={`rx-status-${r.id}`}><SelectValue/></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pending">Pending</SelectItem>
                          <SelectItem value="contacted">Contacted</SelectItem>
                          <SelectItem value="fulfilled">Fulfilled</SelectItem>
                          <SelectItem value="ignored">Ignored</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!preview} onOpenChange={() => setPreview(null)}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-2xl text-[#1A362B]">Prescription from {preview?.name}</DialogTitle>
          </DialogHeader>
          {preview && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="label-caps text-[10px] text-stone-500 block">Phone</span><span className="font-mono-num">+91 {preview.phone}</span></div>
                <div><span className="label-caps text-[10px] text-stone-500 block">Received</span>{new Date(preview.created_at).toLocaleString("en-IN")}</div>
                {preview.note && <div className="col-span-2"><span className="label-caps text-[10px] text-stone-500 block">Note</span><span className="italic">{preview.note}</span></div>}
              </div>
              <div className="rounded-md overflow-hidden border border-stone-200 bg-stone-50">
                <RxPreview path={preview.prescription_path}/>
              </div>
              {preview.ai_result && (
                <details className="text-xs bg-stone-50 border border-stone-200 rounded-md p-3" data-testid="rx-ai-details">
                  <summary className="label-caps text-[10px] text-[#1A362B] cursor-pointer">AI verification detail</summary>
                  <pre className="mt-2 whitespace-pre-wrap font-mono-num text-[11px] text-stone-700">{JSON.stringify(preview.ai_result, null, 2)}</pre>
                </details>
              )}
              <div className="flex items-center gap-2">
                <a href={`https://wa.me/91${preview.phone}`} target="_blank" rel="noopener noreferrer" className="flex-1">
                  <Button className="w-full bg-[#25D366] hover:bg-[#1DA851] text-white"><MessageCircle className="w-4 h-4 mr-2"/> Open WhatsApp</Button>
                </a>
                <Button variant="outline" onClick={() => copyPhone(preview.phone)}><Copy className="w-4 h-4 mr-2"/> Copy number</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function RxThumb({ path }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let cancelled = false;
    let url = null;
    api.get(`/prescriptions/file/${path}`, { responseType: "blob" })
      .then((r) => {
        if (cancelled) return;
        url = URL.createObjectURL(r.data);
        setSrc(url);
      })
      .catch(() => setSrc(null));
    return () => { cancelled = true; if (url) URL.revokeObjectURL(url); };
  }, [path]);
  if (!src) return <div className="w-full h-full bg-stone-100"/>;
  return <img src={src} alt="rx" className="w-full h-full object-cover"/>;
}

function RxPreview({ path }) {
  const [src, setSrc] = useState(null);
  const isPdf = path?.toLowerCase().endsWith(".pdf");
  useEffect(() => {
    let cancelled = false;
    let url = null;
    api.get(`/prescriptions/file/${path}`, { responseType: "blob" })
      .then((r) => {
        if (cancelled) return;
        url = URL.createObjectURL(r.data);
        setSrc(url);
      })
      .catch(() => setSrc(null));
    return () => { cancelled = true; if (url) URL.revokeObjectURL(url); };
  }, [path]);
  if (!src) return <div className="h-96 flex items-center justify-center text-stone-400 text-sm">Loading…</div>;
  return isPdf
    ? <iframe src={src} title="rx" className="w-full h-[70vh]"/>
    : <img src={src} alt="rx" className="w-full max-h-[70vh] object-contain mx-auto"/>;
}



const PROD_REQ_STATUS_META = {
  pending:     { label: "Pending",     className: "status-packed" },
  sourcing:    { label: "Sourcing",    className: "status-dispatched" },
  available:   { label: "Available",   className: "status-delivered" },
  unavailable: { label: "Unavailable", className: "bg-stone-300 text-stone-700" },
};

function ProductRequestsTab() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [busy, setBusy] = useState(false);

  const load = () => {
    setBusy(true);
    api.get("/admin/product-requests", { params: filter !== "all" ? { status: filter } : {} })
      .then(({ data }) => setRows(data))
      .finally(() => setBusy(false));
  };
  useEffect(() => { load(); }, [filter]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/product-requests/${id}`, { status });
      toast.success(`Marked ${status}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };

  const copyPhone = (p) => navigator.clipboard?.writeText(p).then(() => toast.success(`Copied +91 ${p}`));

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex flex-wrap gap-2 items-center">
          {["pending", "sourcing", "available", "unavailable", "all"].map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              className={`px-3 py-1.5 text-xs label-caps rounded-sm border transition-colors ${filter === s ? "bg-[#1A362B] text-[#EFE7D6] border-[#1A362B]" : "bg-white text-stone-600 border-stone-300 hover:border-[#1A362B]"}`}
              data-testid={`pr-filter-${s}`}
            >
              {s}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-stone-600" data-testid="pr-count">{rows.length} request{rows.length === 1 ? "" : "s"}</span>
          <Button size="sm" variant="outline" onClick={load} disabled={busy} className="h-8" data-testid="pr-refresh">
            <RefreshCw className={`w-3.5 h-3.5 mr-1 ${busy ? "animate-spin" : ""}`}/> Refresh
          </Button>
        </div>
      </div>

      <div className="border border-stone-200 rounded-md bg-white overflow-auto thin-scroll">
        <Table>
          <TableHeader>
            <TableRow className="border-stone-200">
              <TableHead className="label-caps text-[10px]">Medicine requested</TableHead>
              <TableHead className="label-caps text-[10px]">Demand</TableHead>
              <TableHead className="label-caps text-[10px]">Requester</TableHead>
              <TableHead className="label-caps text-[10px]">First / Last asked</TableHead>
              <TableHead className="label-caps text-[10px]">Status</TableHead>
              <TableHead className="label-caps text-[10px] text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 && (
              <TableRow><TableCell colSpan={6} className="text-center text-stone-500 py-10">
                <Inbox className="w-6 h-6 mx-auto mb-2 opacity-40"/>
                No {filter !== "all" ? filter : ""} product requests. Shoppers who search for a medicine that isn't in stock can request it from the Shop — those land here.
              </TableCell></TableRow>
            )}
            {rows.map((r) => {
              const meta = PROD_REQ_STATUS_META[r.status] || PROD_REQ_STATUS_META.pending;
              const first = new Date(r.created_at);
              const last = new Date(r.last_requested_at || r.created_at);
              return (
                <TableRow key={r.id} className="border-stone-200" data-testid={`pr-row-${r.id}`}>
                  <TableCell>
                    <div className="font-serif-display text-lg text-[#1A362B] italic">{r.query}</div>
                    {r.note && <div className="text-[11px] text-stone-500 mt-1 italic max-w-[280px] truncate" title={r.note}>{r.note}</div>}
                  </TableCell>
                  <TableCell>
                    <Badge className={`rounded-sm border-0 text-[10px] ${r.count >= 5 ? "bg-[#D96C4A] text-white" : r.count >= 2 ? "status-dispatched" : "bg-stone-200 text-stone-700"}`}>
                      {r.count}× asked
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm text-[#1A362B]">{r.name || "—"}</div>
                    {(r.phones || []).map((p) => (
                      <button key={p} onClick={() => copyPhone(p)} className="text-xs text-stone-500 font-mono-num flex items-center gap-1 hover:text-[#1A362B]" title="Copy">
                        <Phone className="w-3 h-3"/> +91 {p}
                      </button>
                    ))}
                    {(!r.phones || r.phones.length === 0) && <div className="text-xs text-stone-400 italic">No phone</div>}
                  </TableCell>
                  <TableCell>
                    <div className="text-xs text-stone-600">{first.toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}</div>
                    <div className="text-[10px] text-stone-400">last {last.toLocaleDateString("en-IN", { day: "2-digit", month: "short" })} {last.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}</div>
                  </TableCell>
                  <TableCell>
                    <Badge className={`${meta.className} rounded-sm border-0 text-[10px] capitalize`}>{meta.label}</Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="inline-flex items-center gap-1">
                      {(r.phones || []).slice(0, 1).map((p) => (
                        <a key={p} href={`https://wa.me/91${p}`} target="_blank" rel="noopener noreferrer" data-testid={`pr-wa-${r.id}`}>
                          <Button size="icon" variant="ghost" className="h-8 w-8" title="WhatsApp requester"><MessageCircle className="w-4 h-4 text-[#25D366]"/></Button>
                        </a>
                      ))}
                      <Select value={r.status} onValueChange={(v) => updateStatus(r.id, v)}>
                        <SelectTrigger className="h-8 w-[130px] text-xs" data-testid={`pr-status-${r.id}`}><SelectValue/></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pending">Pending</SelectItem>
                          <SelectItem value="sourcing">Sourcing</SelectItem>
                          <SelectItem value="available">Available</SelectItem>
                          <SelectItem value="unavailable">Unavailable</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

